import { useMemo, useState } from "react";
import { Camera, Pencil, Plus, Trash2, Truck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { formatDate, formatNumber } from "@/utils/format";
import {
  createCarrada,
  deleteCarrada,
  groupCarradasEmViagens,
  lancamentoPrincipalViagem,
  quantidadeFaturavelCarradaDto,
  SITUACAO_VIAGEM_LABELS,
  updateCarrada,
  type CaminhaoCarradasDto,
  type CarradaDto,
  type CarradaFormValues,
  type CarradaViagem,
  type CarradaViagemSituacao,
  type ContratoCarradasDto,
} from "@/services/carradas";
import type { PeriodRange } from "@/services/locacoes";
import { CarradaFotoLightbox } from "./CarradaFotoLightbox";
import { LocacoesStatCard } from "./LocacoesStatCard";
import { ModalCarrada } from "./ModalCarrada";
import {
  formatPeriodLabel,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
} from "./locacoesUtils";

const SITUACAO_VIAGEM_CLASS: Record<CarradaViagemSituacao, string> = {
  conferido: "bg-emerald-100 text-emerald-800",
  qtd_diferente: "bg-sky-100 text-sky-800",
  so_jazida: "bg-amber-100 text-amber-800",
  so_obra: "bg-violet-100 text-violet-800",
  manual: "bg-slate-100 text-slate-700",
};

function fotosDaViagem(v: CarradaViagem) {
  const lados: Array<{ lado: "Jazida" | "Obra" | "Manual"; carrada?: CarradaDto }> = [
    { lado: "Jazida", carrada: v.jazida },
    { lado: "Obra", carrada: v.obra },
    { lado: "Manual", carrada: v.manual },
  ];
  return lados.flatMap(({ lado, carrada }) =>
    (carrada?.fotos ?? []).map((foto) => ({
      carradaId: carrada!.id,
      fotoId: foto.id,
      lado,
    })),
  );
}

interface Props {
  contrato: ContratoCarradasDto;
  caminhoes: CaminhaoCarradasDto[];
  /** Todos os lançamentos do contrato (já filtrados por contratoId). */
  lancamentos: CarradaDto[];
  period: PeriodRange;
  busy?: boolean;
  onRefresh: () => void | Promise<void>;
}

export function TabApropriacaoCarradas({
  contrato,
  caminhoes,
  lancamentos,
  period,
  busy,
  onRefresh,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CarradaDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [fotoPreview, setFotoPreview] = useState<{
    items: Array<{ carradaId: string; fotoId: string }>;
    index: number;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CarradaDto | null>(null);

  const caminhoesById = useMemo(() => new Map(caminhoes.map((c) => [c.id, c])), [caminhoes]);

  const noPeriodo = useMemo(
    () =>
      lancamentos.filter(
        (c) =>
          c.data >= period.startDate &&
          c.data <= period.endDate &&
          c.statusAprovacao !== "rejeitado",
      ),
    [lancamentos, period.startDate, period.endDate],
  );

  const viagens = useMemo(() => groupCarradasEmViagens(noPeriodo), [noPeriodo]);

  const carradasNoPeriodo = useMemo(
    () => noPeriodo.reduce((s, c) => s + quantidadeFaturavelCarradaDto(c, noPeriodo), 0),
    [noPeriodo],
  );

  const valorNoPeriodo = useMemo(
    () =>
      noPeriodo.reduce(
        (s, c) => s + quantidadeFaturavelCarradaDto(c, noPeriodo) * (c.valorUnitario || 0),
        0,
      ),
    [noPeriodo],
  );

  const comFoto = useMemo(
    () => viagens.filter((v) => v.lancamentos.some((c) => (c.fotos?.length ?? 0) > 0)).length,
    [viagens],
  );

  const handleSave = async (values: CarradaFormValues, editingId: string | null): Promise<CarradaDto> => {
    setSaving(true);
    try {
      const saved = editingId
        ? await updateCarrada(editingId, values)
        : await createCarrada(values);
      await onRefresh();
      return saved;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c: CarradaDto) => {
    setSaving(true);
    try {
      await deleteCarrada(c.id);
      setModalOpen(false);
      setEditing(null);
      await onRefresh();
    } finally {
      setSaving(false);
      setConfirmDelete(null);
    }
  };

  return (
    <div className={locacoesTabContentClass}>
      <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        Aba de apropriação — cada linha é uma viagem. A partir de 08/10/2026, a carga da Jazida e a
        chegada na Obra do mesmo caminhão, material e dia ficam juntas. Período: {formatPeriodLabel(period)}.
      </p>

      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <LocacoesStatCard
          title="Viagens no período"
          value={viagens.length}
          icon={<Truck className="h-5 w-5" />}
          accentClass="h-full min-w-0 border-l-4 border-l-emerald-400"
        />
        <LocacoesStatCard
          title="Carradas no período"
          value={carradasNoPeriodo}
          icon={<Truck className="h-5 w-5" />}
          accentClass="h-full min-w-0 border-l-4 border-l-blue-400"
        />
        <LocacoesStatCard
          title="Valor no período"
          value={`R$ ${formatNumber(valorNoPeriodo, 2)}`}
          icon={<Truck className="h-5 w-5" />}
          accentClass="h-full min-w-0 border-l-4 border-l-purple-400"
        />
        <LocacoesStatCard
          title="Com foto"
          value={`${comFoto}/${viagens.length}`}
          icon={<Camera className="h-5 w-5" />}
          accentClass="h-full min-w-0 border-l-4 border-l-sky-400"
        />
      </div>

      <div className="flex justify-end">
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          disabled={busy || saving}
        >
          Nova carrada
        </Button>
      </div>

      <div className={locacoesTableWrapClass}>
        <table className="min-w-full text-sm">
          <thead className={locacoesTableHeadClass}>
            <tr>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Máquina</th>
              <th className="px-4 py-3">Operador</th>
              <th className="px-4 py-3">Placa</th>
              <th className="px-4 py-3">Material</th>
              <th className="px-4 py-3">Viagem</th>
              <th className="px-4 py-3">Qtd</th>
              <th className="px-4 py-3">Vl. unit.</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Anexos</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {viagens.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-8 text-center text-slate-500">
                  Nenhuma carrada no período selecionado.
                </td>
              </tr>
            ) : (
              viagens.map((v) => {
                const maquina = caminhoesById.get(v.caminhaoId);
                const principal = lancamentoPrincipalViagem(v);
                const fotos = fotosDaViagem(v);
                const total = principal.quantidade * (principal.valorUnitario || 0);
                const operadores = [
                  ...new Set(
                    v.lancamentos
                      .map((c) => (c.motorista || "").trim())
                      .filter(Boolean),
                  ),
                ];
                return (
                  <tr key={v.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 text-slate-700">{formatDate(v.data)}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {maquina?.nome ?? v.caminhaoId}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{operadores.join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{maquina?.placa || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{v.material || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ${SITUACAO_VIAGEM_CLASS[v.situacao]}`}
                      >
                        {SITUACAO_VIAGEM_LABELS[v.situacao]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {principal.quantidade}
                      {v.situacao === "qtd_diferente" && (
                        <span className="mt-0.5 block text-xs text-slate-500">
                          Jazida {v.jazida?.quantidade} · Obra {v.obra?.quantidade}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-700">
                      {principal.valorUnitario > 0
                        ? `R$ ${formatNumber(principal.valorUnitario, 2)}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 tabular-nums font-medium text-slate-800">
                      {total > 0 ? `R$ ${formatNumber(total, 2)}` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {fotos.length === 0 ? (
                        <span className="text-amber-700">Sem foto</span>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                          onClick={() =>
                            setFotoPreview({
                              items: fotos,
                              index: 0,
                            })
                          }
                        >
                          <Camera className="h-3.5 w-3.5" />
                          {fotos.length}
                          {v.jazida && v.obra ? " · J+O" : ""}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {v.jazida && v.obra ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Pencil className="h-3.5 w-3.5" />}
                            disabled={busy || saving}
                            onClick={() => {
                              setEditing(v.jazida ?? null);
                              setModalOpen(true);
                            }}
                          >
                            Jazida
                          </Button>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Pencil className="h-3.5 w-3.5" />}
                          disabled={busy || saving}
                          onClick={() => {
                            setEditing(principal);
                            setModalOpen(true);
                          }}
                        >
                          {v.jazida && v.obra ? "Obra" : "Editar"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          className="text-red-600"
                          disabled={busy || saving}
                          onClick={() => setConfirmDelete(principal)}
                        >
                          Excluir
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ModalCarrada
        open={modalOpen}
        editing={editing}
        contratos={[contrato]}
        caminhoes={caminhoes}
        busy={saving}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
          void onRefresh();
        }}
        onSave={handleSave}
        onDelete={editing ? () => setConfirmDelete(editing) : undefined}
      />

      {fotoPreview && (
        <CarradaFotoLightbox
          items={fotoPreview.items}
          initialIndex={fotoPreview.index}
          onClose={() => setFotoPreview(null)}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(confirmDelete)}
        title="Excluir carrada"
        message={
          confirmDelete?.origem === "obra" &&
          noPeriodo.some(
            (c) => c.id !== confirmDelete.id && c.parId && c.parId === confirmDelete.parId && c.origem === "jazida",
          )
            ? "Remover o lançamento da Obra? O da Jazida permanece, com a foto."
            : "Remover este lançamento de carrada?"
        }
        confirmLabel="Excluir"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) void handleDelete(confirmDelete);
        }}
      />
    </div>
  );
}
